import React from 'react';
import {
    ChevronRightIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    PlayCircleIcon
} from '@heroicons/react/24/solid'; // Using solid for filled icons as in original

// If `allClassifications` prop is not passed, provide a default or handle its absence.
const PlayerCard = ({ 
    player, 
    stats, 
    snippets, 
    isExpanded, 
    onToggleExpand, 
    onPlaySnippet, 
    onPlayerNameClick, // Assuming navigation is handled by parent
    selectedClassifications, 
    allClassifications = [] // Default to empty array if not provided from parent
}) => {
    // Filter for high value snippets only for the expansion display
    let highValueSnippets = snippets?.filter(s => s.value?.toLowerCase() !== 'low') || [];
    if (selectedClassifications && selectedClassifications.size > 0) {
        highValueSnippets = highValueSnippets.filter(s => selectedClassifications.has(s.classification?.trim() || "Unclassified"));
    }

    return (
        <div className="card bg-base-100 shadow-md mb-4">
            <div className="card-body p-4">
                <div className="flex justify-between items-center">
                    <div>
                        <button
                            className="font-bold text-lg text-base-content hover:text-primary focus:outline-none"
                            onClick={() => onPlayerNameClick(player.id)}
                        >
                            {player.name}
                        </button>
                        <p className="text-xs text-base-content/70">{player.position || 'N/A'} - {player.team || 'N/A'}</p>
                    </div>
                    <button className="btn btn-ghost btn-xs p-1" onClick={() => onPlayerNameClick(player.id)}>
                        <ChevronRightIcon className="w-5 h-5 text-primary"/>
                    </button>
                </div>
                <div className="divider my-2"></div>
                <div className="grid grid-cols-3 gap-2 text-center text-xs mb-2">
                    <div><div className="font-medium text-base-content/70">Total</div><div className="text-lg font-semibold">{stats.total}</div></div>
                    <div><div className="font-medium text-success/80">Pos</div><div className="text-lg font-semibold text-success">{stats.positive}</div></div>
                    <div><div className="font-medium text-error/80">Neg</div><div className="text-lg font-semibold text-error">{stats.negative}</div></div>
                </div>
                {allClassifications.length > 0 && stats.classifications && (
                    <>
                        <div className="divider my-1"></div>
                        <p className="text-xs font-medium text-base-content/70 mb-1">Classifications:</p>
                        <div className="flex flex-wrap gap-1">
                            {allClassifications.map(cls => {
                                const count = stats.classifications[cls] || 0;
                                if (count > 0) {
                                    return (<div key={cls} className="badge badge-outline badge-sm">{cls}: {count}</div>);
                                }
                                return null;
                            })}
                        </div>
                    </>
                )}

                {/* Snippet Expansion Section */} 
                {highValueSnippets.length > 0 && ( 
                    <div className="mt-3 pt-3 border-t border-base-300"> 
                        <button 
                            onClick={onToggleExpand} 
                            className="flex justify-between items-center w-full text-left text-sm font-medium text-primary hover:text-primary-focus focus:outline-none" 
                        > 
                            <span>High Value Snippets ({highValueSnippets.length})</span> 
                            {isExpanded ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />} 
                        </button> 

                        {isExpanded && ( 
                            <div className="mt-2 max-h-60 overflow-y-auto space-y-2 pr-1"> 
                                {highValueSnippets.map(snippet => ( 
                                    <div key={snippet.id} className="text-xs p-2 bg-base-200 rounded-lg shadow-sm"> 
                                        <p className="text-base-content/90 leading-snug whitespace-pre-wrap mb-1.5">{snippet.snippet}</p> 
                                        <div className="flex justify-between items-center text-xs text-base-content/70 gap-2"> 
                                            <span> 
                                                Sentiment: <span className={`font-medium ${ 
                                                    snippet.sentiment?.toLowerCase() === 'positive' ? 'text-success' : 
                                                    snippet.sentiment?.toLowerCase() === 'negative' ? 'text-error' : 
                                                    'text-base-content/80' 
                                                }`}>{snippet.sentiment || 'N/A'}</span> 
                                            </span> 
                                            <button 
                                                onClick={() => onPlaySnippet(snippet)} 
                                                className="btn btn-xs btn-ghost text-primary hover:bg-primary/10 p-1 ml-auto" 
                                                title="Play Snippet" 
                                            > 
                                                <PlayCircleIcon className="w-5 h-5" /> 
                                            </button> 
                                        </div> 
                                        <div className="text-xs text-base-content/70 mt-1"> 
                                            Aired: {snippet.video_upload_date ? new Date(snippet.video_upload_date).toLocaleDateString() : (snippet.video?.date ? new Date(snippet.video.date).toLocaleDateString() : 'Unknown Date')} 
                                        </div> 
                                    </div> 
                                ))} 
                            </div> 
                        )} 
                    </div> 
                )} 
            </div>
        </div>
    );
};

export default PlayerCard; 